import { useState } from 'react';
import type { MenuItem, MenuGroup, CartAddon } from '../types';
import { useMenu } from '../context/MenuContext';
import { useCart } from '../context/CartContext';
import { useI18n } from '../i18n';

interface ComplementPickerProps {
  item: MenuItem;
  complementGroups: MenuGroup[];
  onClose: () => void;
}

export function ComplementPicker({ item, complementGroups, onClose }: ComplementPickerProps) {
  const { getGroupItems } = useMenu();
  const { addItem } = useCart();
  const { t } = useI18n();
  const [selectedAddons, setSelectedAddons] = useState<Map<number, CartAddon[]>>(new Map());
  const [note, setNote] = useState('');
  const [quantity, setQuantity] = useState(1);

  function toggleAddon(group: MenuGroup, addonItem: MenuItem) {
    setSelectedAddons((prev) => {
      const next = new Map(prev);
      const current = next.get(group.id) || [];
      const exists = current.find((a) => a.id === addonItem.id);

      if (exists) {
        next.set(group.id, current.filter((a) => a.id !== addonItem.id));
      } else if (group.multi) {
        next.set(group.id, [...current, { id: addonItem.id, name: addonItem.name, price: Number(addonItem.price) }]);
      } else {
        // Single-select: replace
        next.set(group.id, [{ id: addonItem.id, name: addonItem.name, price: Number(addonItem.price) }]);
      }
      return next;
    });
  }

  function isSelected(groupId: number, addonItemId: number) {
    return (selectedAddons.get(groupId) || []).some((a) => a.id === addonItemId);
  }

  function handleAdd() {
    const allAddons: CartAddon[] = [];
    selectedAddons.forEach((addons) => allAddons.push(...addons));
    const trimmedNote = note.trim() || undefined;
    for (let i = 0; i < quantity; i++) {
      addItem(item, allAddons, trimmedNote);
    }
    onClose();
  }

  const addonTotal = Array.from(selectedAddons.values())
    .flat()
    .reduce((s, a) => s + a.price, 0);

  return (
    <div className="hc-modal-overlay" onClick={onClose}>
      <div className="hc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="hc-modal-header">
          <h2>{item.name}</h2>
          <button className="hc-modal-close" onClick={onClose}>&times;</button>
        </div>

        {item.description && <p className="hc-modal-desc">{item.description}</p>}

        <div className="hc-complement-groups">
          {complementGroups.map((group) => {
            const groupItems = getGroupItems(group.id);
            if (groupItems.length === 0) return null;

            return (
              <div key={group.id} className="hc-complement-group">
                <h3 className="hc-complement-group-name">
                  {group.name}
                  <span className="hc-complement-group-hint">
                    {group.multi ? t('picker.selectMultiple') : t('picker.selectOne')}
                  </span>
                </h3>
                <div className="hc-complement-options">
                  {groupItems.map((gi) => (
                    <button
                      key={gi.id}
                      className={`hc-complement-option ${isSelected(group.id, gi.id) ? 'hc-selected' : ''}`}
                      onClick={() => toggleAddon(group, gi)}
                    >
                      <span className="hc-complement-option-name">{gi.name}</span>
                      {Number(gi.price) > 0 && (
                        <span className="hc-complement-option-price">+{Number(gi.price).toFixed(2)} &euro;</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="hc-item-note-group">
          <label>{t('picker.note')}</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('picker.notePlaceholder')}
            maxLength={255}
          />
        </div>

        <div className="hc-modal-footer">
          <span className="hc-modal-total">
            {((Number(item.price) + addonTotal) * quantity).toFixed(2)} &euro;
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="hc-qty-controls">
              <button className="hc-qty-btn" onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
              <span className="hc-qty-value">{quantity}</span>
              <button className="hc-qty-btn" onClick={() => setQuantity(quantity + 1)}>+</button>
            </div>
            <button className="hc-btn hc-btn-primary" onClick={handleAdd}>
              {t('picker.addToCart')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
